import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Link2, Link2Off, FileText, FileX, Clock, ChevronLeft, ChevronRight, X,
  ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

// Sıralanabilir sütunlar — `sort_by` değerleri sunucudaki BEYAZ LİSTEyle aynı
// (invoices.py → SIRALAMA_ALANLARI). "Eşleşme" bilerek dışarıda: zaten kendi
// süzgeci var, sıralaması bir şey katmıyor.
const SUTUNLAR = [
  { anahtar: 'invoice_number', etiket: 'Fatura No' },
  { anahtar: 'supplier', etiket: 'Tedarikci' },
  { anahtar: 'invoice_date', etiket: 'Fatura Tarihi' },
  { anahtar: 'due_date', etiket: 'Vade Tarihi' },
  { anahtar: 'currency', etiket: 'PB' },
  { anahtar: 'amount_foreign', etiket: 'Tutar', sag: true },
  { anahtar: 'amount_try', etiket: 'TL Karsiligi', sag: true },
  { anahtar: 'invoice_type', etiket: 'Fatura Turu' },
];

// Sütun filtre kutularının ortak sınıfı (dar, tablo başlığına sığan)
const KUTU = 'h-7 w-full px-1.5 rounded border bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]';

// `draft`: kullanicinin kaydettigi degil, "yeni urun/tedarikci olustur"
// sirasinda is kaybolmasin diye otomatik yazilan kayit. Varsayilan listede
// gorunmez; yalnizca "Taslaklar" secilince gelir.
const statusLabels = { paid: 'Odendi', pending: 'Bekliyor', overdue: 'Gecikti', draft: 'Taslak' };
const statusColors = {
  paid: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  pending: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  overdue: 'bg-[#7a3535]/10 text-[#7a3535]',
  draft: 'bg-[#8a7e70]/15 text-[#5a5148] border border-[#8a7e70]/40'
};

const invoiceTypeLabels = { invoiced: 'Faturali', no_invoice: 'Faturasiz', pending_invoice: 'Fatura Bekleniyor' };
const invoiceTypeColors = {
  invoiced: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  no_invoice: 'bg-[#8a7e70]/10 text-[#8a7e70]',
  pending_invoice: 'bg-[#7a6a2e]/10 text-[#7a6a2e]'
};
const invoiceTypeIcons = { invoiced: FileText, no_invoice: FileX, pending_invoice: Clock };

const PAGE_SIZE = 50;

export default function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currFilter, setCurrFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const searchTimer = useRef(null);

  // SÜTUN FİLTRELERİ — süzme SUNUCUDA yapılır (Kasa/Banka Hareketler ile aynı
  // desen). İstemcide süzmek yanıltıcı olurdu: yalnızca açık olan SAYFA
  // süzülür, öbür sayfalardaki eşleşmeler gizli kalırdı.
  const BOS_SUTUN = {
    invoice_number: '', supplier_search: '',
    date_from: '', date_to: '', due_from: '', due_to: '',
    amount_op: '', amount: '', amount2: '',
    try_op: '', try_amount: '', try_amount2: '',
    matched: 'all',
  };
  // Sıralama SUNUCUDA: liste sayfalı, açık sayfayı sıralamak "en yüksek tutar"
  // sorusuna yanlış cevap verirdi. Varsayılan fatura tarihi, yeniden eskiye.
  const [sira, setSira] = useState({ by: 'invoice_date', dir: 'desc' });
  const [sutun, setSutun] = useState(BOS_SUTUN);            // uygulanan
  const [sutunTaslak, setSutunTaslak] = useState(BOS_SUTUN); // kutulara yazılan
  const sutunZaman = useRef(null);

  const fetchInvoices = useCallback(async (pg, srch, status, curr, type, f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (srch) params.set('search', srch);
      if (status !== 'all') params.set('status', status);
      if (curr !== 'all') params.set('currency', curr);
      if (type !== 'all') params.set('invoice_type', type);
      const k = f || BOS_SUTUN;
      ['invoice_number', 'supplier_search', 'date_from', 'date_to',
       'due_from', 'due_to'].forEach(alan => { if (k[alan]) params.set(alan, k[alan]); });
      if (k.matched !== 'all') params.set('matched', k.matched);
      // Tutar: virgüllü yazım da kabul edilir (1.234,56 → 1234.56)
      const sayiYaz = (op, a, b, adlar) => {
        if (!op || a === '' || a == null) return;
        params.set(adlar[0], op);
        params.set(adlar[1], String(a).replace(/\./g, '').replace(',', '.'));
        if (op === 'between' && b !== '') {
          params.set(adlar[2], String(b).replace(/\./g, '').replace(',', '.'));
        }
      };
      sayiYaz(k.amount_op, k.amount, k.amount2, ['amount_op', 'amount', 'amount2']);
      sayiYaz(k.try_op, k.try_amount, k.try_amount2, ['try_op', 'try_amount', 'try_amount2']);
      if (sira.by) { params.set('sort_by', sira.by); params.set('sort_dir', sira.dir); }
      const r = await api.get(`/invoices?${params}`);
      setInvoices(r.data.invoices || []);
      setTotal(r.data.total || 0);
    } catch {}
    setLoading(false);
  }, [sira]);

  useEffect(() => {
    fetchInvoices(page, search, statusFilter, currFilter, typeFilter, sutun);
    // `search` bilerek dışarıda: kendi gecikmesiyle çağırıyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, currFilter, typeFilter, sutun, sira]);

  // Aynı sütuna ikinci tıklama yönü çevirir; yeni sütun artan başlar.
  // Tarih ve tutar sütunlarında ilk tıklama AZALAN: "en yeni" ve "en büyük"
  // bu listelerde beklenen ilk sonuç.
  const siralamayiDegistir = (anahtar) => {
    setPage(1);
    setSira(onceki => {
      if (onceki.by === anahtar) {
        return { by: anahtar, dir: onceki.dir === 'asc' ? 'desc' : 'asc' };
      }
      const azalanBaslasin = ['invoice_date', 'due_date', 'amount_foreign', 'amount_try'];
      return { by: anahtar, dir: azalanBaslasin.includes(anahtar) ? 'desc' : 'asc' };
    });
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchInvoices(1, val, statusFilter, currFilter, typeFilter, sutun);
    }, 400);
  };

  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  // Yazarken her tuşta istek atmasın; tarih/seçim değişimi ANLIK uygulanır
  const sutunDegistir = (alan, deger, anlik = false) => {
    setSutunTaslak(prev => {
      const yeni = { ...prev, [alan]: deger };
      clearTimeout(sutunZaman.current);
      setPage(1);
      if (anlik) setSutun(yeni);
      else sutunZaman.current = setTimeout(() => setSutun(yeni), 400);
      return yeni;
    });
  };

  const sutunTemizle = () => {
    clearTimeout(sutunZaman.current);
    setSutunTaslak(BOS_SUTUN);
    setSutun(BOS_SUTUN);
    setPage(1);
  };

  const sutunFiltreVar = Object.keys(BOS_SUTUN)
    .some(k => (k === 'matched' ? sutunTaslak[k] !== 'all' : sutunTaslak[k]));

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="invoice-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Faturalar</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{total} fatura</p>
        </div>
        <Link to="/erp/invoices/new">
          <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-invoice-btn">
            <Plus size={16} className="mr-2" /> Yeni Fatura
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Fatura no veya tedarikci ara..." value={search} onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 bg-white border-[#d0c4b0]" data-testid="invoice-search" />
        </div>
        <Select value={currFilter} onValueChange={handleFilterChange(setCurrFilter)}>
          <SelectTrigger className="w-28 bg-white border-[#d0c4b0]"><SelectValue placeholder="PB" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            <SelectItem value="TRY">TRY</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={handleFilterChange(setTypeFilter)}>
          <SelectTrigger className="w-40 bg-white border-[#d0c4b0]" data-testid="invoice-type-filter"><SelectValue placeholder="Fatura Turu" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Turler</SelectItem>
            <SelectItem value="invoiced">Faturali</SelectItem>
            <SelectItem value="no_invoice">Faturasiz</SelectItem>
            <SelectItem value="pending_invoice">Fatura Bekleniyor</SelectItem>
          </SelectContent>
        </Select>
        {sutunFiltreVar && (
          <Button variant="outline" onClick={sutunTemizle}
            className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10"
            data-testid="f-temizle">
            <X size={14} className="mr-1" /> Sütun filtrelerini temizle
          </Button>
        )}
        <Select value={statusFilter} onValueChange={handleFilterChange(setStatusFilter)}>
          <SelectTrigger className="w-36 bg-white border-[#d0c4b0]"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Durumlar</SelectItem>
            <SelectItem value="paid">Odendi</SelectItem>
            <SelectItem value="pending">Bekliyor</SelectItem>
            <SelectItem value="overdue">Gecikti</SelectItem>
            <SelectItem value="draft">Taslaklar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              {SUTUNLAR.map(({ anahtar, etiket, sag }) => (
                <TableHead key={anahtar}
                  onClick={() => siralamayiDegistir(anahtar)}
                  title={`${etiket} sütununa göre sırala`}
                  className={`text-[#1a1916] font-bold text-xs uppercase cursor-pointer select-none
                              hover:bg-[#d8cfc0] transition-colors ${sag ? 'text-right' : ''}`}
                  data-testid={`sirala-${anahtar}`}>
                  <span className={`inline-flex items-center gap-1 ${sag ? 'flex-row-reverse' : ''}`}>
                    {etiket}
                    {sira.by === anahtar
                      ? (sira.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                      : <ArrowUpDown size={11} className="text-[#b0a898]" />}
                  </span>
                </TableHead>
              ))}
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Eslesme</TableHead>
            </TableRow>
            {/* SÜTUN FİLTRELERİ — sunucuda uygulanır, tüm kayıtları kapsar.
                Tarih sütunlarında gerçek tarih kutusu, tutar sütunlarında
                karşılaştırma + sayı (Kasa/Banka Hareketler ile aynı desen). */}
            <TableRow className="bg-[#f0e9dc] hover:bg-[#f0e9dc] border-[#d0c4b0]">
              <TableCell className="py-1.5 px-2 align-top w-[130px]">
                <input type="text" value={sutunTaslak.invoice_number}
                  onChange={e => sutunDegistir('invoice_number', e.target.value)}
                  placeholder="Fatura no" title="Fatura numarasında ara"
                  className={`${KUTU} ${sutunTaslak.invoice_number ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                  data-testid="f-fatura-no" />
              </TableCell>
              <TableCell className="py-1.5 px-2 align-top min-w-[160px]">
                <input type="text" value={sutunTaslak.supplier_search}
                  onChange={e => sutunDegistir('supplier_search', e.target.value)}
                  placeholder="Tedarikçi adı / kodu" title="Cari adı, kodu ya da faturadan okunan ad"
                  className={`${KUTU} ${sutunTaslak.supplier_search ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                  data-testid="f-tedarikci" />
              </TableCell>
              <TableCell className="py-1.5 px-2 align-top w-[140px]">
                <div className="flex flex-col gap-1">
                  <input type="date" value={sutunTaslak.date_from}
                    onChange={e => sutunDegistir('date_from', e.target.value, true)}
                    title="Fatura tarihi — başlangıç"
                    className={`${KUTU} ${sutunTaslak.date_from ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                    data-testid="f-tarih-bas" />
                  <input type="date" value={sutunTaslak.date_to}
                    onChange={e => sutunDegistir('date_to', e.target.value, true)}
                    title="Fatura tarihi — bitiş"
                    className={`${KUTU} ${sutunTaslak.date_to ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                    data-testid="f-tarih-bit" />
                </div>
              </TableCell>
              <TableCell className="py-1.5 px-2 align-top w-[140px]">
                <div className="flex flex-col gap-1">
                  <input type="date" value={sutunTaslak.due_from}
                    onChange={e => sutunDegistir('due_from', e.target.value, true)}
                    title="Vade tarihi — başlangıç"
                    className={`${KUTU} ${sutunTaslak.due_from ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                    data-testid="f-vade-bas" />
                  <input type="date" value={sutunTaslak.due_to}
                    onChange={e => sutunDegistir('due_to', e.target.value, true)}
                    title="Vade tarihi — bitiş"
                    className={`${KUTU} ${sutunTaslak.due_to ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                    data-testid="f-vade-bit" />
                </div>
              </TableCell>
              {/* PB: üstteki açılır menüyle AYNI durum — iki kutu tek filtre */}
              <TableCell className="py-1.5 px-2 align-top w-[80px]">
                <select value={currFilter}
                  onChange={e => handleFilterChange(setCurrFilter)(e.target.value)}
                  className={`${KUTU} ${currFilter !== 'all' ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                  data-testid="f-pb">
                  <option value="all">Tümü</option>
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </TableCell>
              {[['amount_op', 'amount', 'amount2', 'f-tutar'],
                ['try_op', 'try_amount', 'try_amount2', 'f-tl']].map(([op, a, b, tid]) => (
                <TableCell key={op} className="py-1.5 px-2 align-top w-[150px]">
                  <div className="flex flex-col gap-1">
                    <select value={sutunTaslak[op]}
                      onChange={e => sutunDegistir(op, e.target.value, true)}
                      className={`${KUTU} ${sutunTaslak[op] ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                      data-testid={`${tid}-op`}>
                      <option value="">Karşılaştır…</option>
                      <option value="=">= eşit</option>
                      <option value=">">&gt; büyük</option>
                      <option value=">=">&ge; büyük eşit</option>
                      <option value="<">&lt; küçük</option>
                      <option value="<=">&le; küçük eşit</option>
                      <option value="between">aralık</option>
                    </select>
                    <input type="text" inputMode="decimal" value={sutunTaslak[a]}
                      onChange={e => sutunDegistir(a, e.target.value)}
                      placeholder="0,00" disabled={!sutunTaslak[op]}
                      className={`${KUTU} text-right ${sutunTaslak[a] ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'} disabled:bg-[#f5f0e8] disabled:text-[#c0b8ad]`}
                      data-testid={tid} />
                    {sutunTaslak[op] === 'between' && (
                      <input type="text" inputMode="decimal" value={sutunTaslak[b]}
                        onChange={e => sutunDegistir(b, e.target.value)}
                        placeholder="üst sınır"
                        className={`${KUTU} text-right ${sutunTaslak[b] ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                        data-testid={`${tid}-2`} />
                    )}
                  </div>
                </TableCell>
              ))}
              {/* Fatura türü: üstteki açılır menüyle aynı durum */}
              <TableCell className="py-1.5 px-2 align-top w-[150px]">
                <select value={typeFilter}
                  onChange={e => handleFilterChange(setTypeFilter)(e.target.value)}
                  className={`${KUTU} ${typeFilter !== 'all' ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                  data-testid="f-tur">
                  <option value="all">Tüm türler</option>
                  <option value="invoiced">Faturalı</option>
                  <option value="no_invoice">Faturasız</option>
                  <option value="pending_invoice">Fatura bekleniyor</option>
                </select>
              </TableCell>
              <TableCell className="py-1.5 px-2 align-top w-[120px]">
                <select value={sutunTaslak.matched}
                  onChange={e => sutunDegistir('matched', e.target.value, true)}
                  className={`${KUTU} ${sutunTaslak.matched !== 'all' ? 'border-[#3d6e4e]' : 'border-[#d0c4b0]'}`}
                  data-testid="f-eslesme">
                  <option value="all">Tümü</option>
                  <option value="true">Eşleşti</option>
                  <option value="false">Bekliyor</option>
                </select>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#8a7e70]">Yukleniyor...</TableCell></TableRow>
            )}
            {!loading && invoices.map(inv => {
              const invType = inv.invoice_type || 'invoiced';
              const TypeIcon = invoiceTypeIcons[invType] || FileText;
              return (
                <TableRow key={inv.id} className="erp-table-row border-[#d0c4b0] cursor-pointer"
                  // Fatura yeni sekmede açılır — liste, filtre ve sayfa konumu korunsun
                  onClick={() => window.open(`/erp/invoices/${inv.id}`, '_blank', 'noopener')}>
                  <TableCell className="font-mono text-sm text-[#3F3E39] font-medium hover:underline">{inv.invoice_number || '-'}</TableCell>
                  <TableCell className="font-medium text-[#1a1916]">{inv.supplier_name}</TableCell>
                  <TableCell className="text-sm text-[#3F3E39]">{inv.invoice_date || '-'}</TableCell>
                  <TableCell className="text-sm text-[#3F3E39]">{inv.due_date || '-'}</TableCell>
                  <TableCell className="text-sm font-semibold">{inv.currency}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{inv.amount_foreign?.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-[#3F3E39]">{inv.amount_try?.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`status-badge inline-flex items-center gap-1 ${invoiceTypeColors[invType] || ''}`}>
                      <TypeIcon size={12} />
                      {invoiceTypeLabels[invType] || invType}
                    </span>
                  </TableCell>
                  <TableCell>
                    {inv.supplier_id ? (
                      <Link2 size={14} className="text-[#3d6e4e]" title="Tedarikci eslesti" />
                    ) : (
                      <Link2Off size={14} className="text-[#7a6a2e]" title="Eslesme bekliyor" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && invoices.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#8a7e70]">Fatura bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-[#8a7e70]">
          <span>Sayfa {page} / {pages} ({total} fatura)</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-[#d0c4b0]"
              disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </Button>
            {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
              let pg;
              if (pages <= 7) pg = i + 1;
              else if (page <= 4) pg = i + 1;
              else if (page >= pages - 3) pg = pages - 6 + i;
              else pg = page - 3 + i;
              return (
                <Button key={pg} variant={pg === page ? 'default' : 'outline'} size="sm"
                  className={`h-8 w-8 p-0 ${pg === page ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'border-[#d0c4b0] text-[#3F3E39]'}`}
                  onClick={() => setPage(pg)}>
                  {pg}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-[#d0c4b0]"
              disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
